/* istanbul ignore file */
import _ from 'lodash';
import React, { useState, useMemo, useCallback} from 'react';
import { DROPDOWN_MENU_HEIGHTS } from '../common/utils';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FixedSizeList } from 'react-window';

const partialMenuHeight = DROPDOWN_MENU_HEIGHTS.REACT_WINDOW_LIST_MENU;

export function ReactWindowList({
    id,
    listOptions = [],
    selectedKeys,
    cursor,
    minWidth = 200,
    maxWidth = 1000,
    
    itemsRenderer,
    onToggleItem,
    keyLabel,
    onHovered,
}) {

    // calculate defaultWidth only once
    const defaultWidth = useMemo(() => {
        let maxStringLength = 0;
        if (_.chain(listOptions).first().get('label').isString().value()) {
            maxStringLength = _.reduce(listOptions, (acc, {label}) => {
                if (_.isString(label) && acc < label.length) return label.length;
                return acc;
            }, 0);
        }
        // This number 8 (avg character width in pix) is deduced based on some examples and is a guess num
        // If there are too many characters like [.,|il] the length might not be accurate
        const newMaxLength = Math.round(maxStringLength * 8) + 48; // 48 = padding + checkbox + annotation tag etc.,
        if (newMaxLength > maxWidth) return maxWidth;
        if (newMaxLength > minWidth) return newMaxLength;
        return minWidth;
    }, []); // eslint-disable-line

    const [width, setWidth] = useState(defaultWidth || minWidth);

    // this is additional check to update the width when the column picker is scrolling
    // ideally this will never update the width because the default width we have set above 
    // should be able to handle most of the cases. But if there are any special cases, below code will deal with it.
    const onItemsRendered = useCallback(({
        visibleStartIndex,
        visibleStopIndex
    }) => {
        let newWidth = width;
        // find the max width and set it for the container if the above default width is not good enough
        try {
            for (let i = visibleStartIndex; i <= visibleStopIndex; i++) {
                const liSelector = `.js-navigable-item-${_.kebabCase(id)}-${i} > div > label`,
                    liElement = document.querySelector(liSelector);
                if (liElement) {
                    const liWidth = document.querySelector(liSelector).clientWidth + 32; // padding
                    if (newWidth < liWidth) newWidth = liWidth;
                }
            }
            if (newWidth > maxWidth) setWidth(maxWidth);
            else if (newWidth > width) setWidth(newWidth);
        }
        catch (e) {
            window.CIG_LOG.error(e);
        }
    }, []); // eslint-disable-line

    return (
        <FixedSizeList
            className={`droplist-items scrollable-menu js-navigable-menu-container-${_.kebabCase(id)}`}
            height={partialMenuHeight}
            itemCount={listOptions.length}
            width={width}
            onItemsRendered={onItemsRendered}
            itemSize={30}>
            {({index, style}) => {
                const data = listOptions[index];
                if (_.isFunction(itemsRenderer)) return itemsRenderer({data, index, style});

                const {label, key, title = key} = data;
                const isChecked = _.includes(selectedKeys, key);
                return (
                    <li
                        key={key}
                        title={title}
                        style={style}
                        className={classnames(
                            `js-navigable-item-${_.kebabCase(id)}-${index}`,
                            {'tw-bg-gray-100 dark:tw-bg-gray-700': index === cursor}
                        )}
                        onMouseEnter={() => onHovered(index)}
                        onMouseLeave={() => onHovered(undefined)}
                        onClick={_.partial(onToggleItem, key, !isChecked)}>
                        <div className="tw-w-auto tw-inline-block">
                            <input readOnly
                                type="checkbox"
                                value={key}
                                className="checkbox toggle-column-display"
                                checked={isChecked}
                                id={`${key}-id`}></input>
                            <label id={`${key}-label-id`}>{keyLabel(key, label)}</label>
                        </div>
                    </li>
                );
            }}
        </FixedSizeList>
    );
}

ReactWindowList.displayName = 'ReactWindowList';
ReactWindowList.propTypes = {
    id: PropTypes.string.isRequired,
    listOptions: PropTypes.array.isRequired,
    selectedKeys: PropTypes.array,
    cursor: PropTypes.number,

    onToggleItem: PropTypes.func,
    keyLabel: PropTypes.func,
    onHovered: PropTypes.func,
    minWidth: PropTypes.number,
    maxWidth: PropTypes.number,
    itemsRenderer: PropTypes.func,

};
